import { useEffect, useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// Module-level state — survives React.StrictMode's double-mount in dev
// and is reset only by a true page reload (fresh JS context).
let HAS_INIT = false;
let LAST_KEY = null;

/**
 * SPA Scroll behavior:
 *  - Reload (F5 / cmd-R / fresh load)          → scroll to top (always Y=0)
 *  - Internal navigation (PUSH / REPLACE)      → scroll to top
 *  - Browser back / forward (POP)              → restore previous scroll position
 *  - Anchor link (#hash)                       → scroll to the hash target
 *
 * Why save on mousedown capture + pagehide instead of on scroll events:
 *   When React unmounts a long page, the DOM shrinks and the browser auto-
 *   clamps window.scrollY to the new max before our cleanup fires. A scroll-
 *   based save would persist that clamped value. Instead we snapshot scrollY
 *   on `mousedown` capture (before any Link handler runs) and on `pagehide`,
 *   which gives us the real pre-navigation position.
 *
 * Why module-level HAS_INIT:
 *   React.StrictMode mounts → unmounts → re-mounts the component in dev.
 *   A component-local ref gets reset on the second mount, so the second
 *   invocation would wrongly treat initial `POP` as a browser-back and
 *   restore from sessionStorage. Module scope persists across the double-
 *   mount but is wiped by a real page reload (new JS context).
 *
 * Why clear sessionStorage on first render:
 *   Guarantees that even if some other code (or StrictMode) hits the POP
 *   restore path on the very first render after a reload, it reads null and
 *   falls through to `scrollTo(0, 0)`.
 */
export default function ScrollManager() {
  const { pathname, search, hash } = useLocation();
  const navType = useNavigationType();

  // Disable the browser's own scroll restoration — we handle it manually.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Save pre-navigation scroll on mousedown / touchstart / keydown / pagehide /
  // popstate. mousedown fires BEFORE click, so it beats React Router's Link
  // handler and captures the real pre-navigation scroll position.
  useEffect(() => {
    const snapshot = () => {
      const key = `scroll:${window.location.pathname}${window.location.search}`;
      sessionStorage.setItem(key, String(window.scrollY));
    };
    document.addEventListener("mousedown", snapshot, { capture: true });
    document.addEventListener("touchstart", snapshot, { capture: true, passive: true });
    document.addEventListener("keydown", snapshot, { capture: true });
    window.addEventListener("pagehide", snapshot);
    window.addEventListener("popstate", snapshot);
    return () => {
      document.removeEventListener("mousedown", snapshot, { capture: true });
      document.removeEventListener("touchstart", snapshot, { capture: true });
      document.removeEventListener("keydown", snapshot, { capture: true });
      window.removeEventListener("pagehide", snapshot);
      window.removeEventListener("popstate", snapshot);
    };
  }, []);

  useLayoutEffect(() => {
    // Set manual mode as early as possible so the browser doesn't auto-restore
    // scroll on reload AFTER we've reset it.
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const key = `scroll:${pathname}${search}`;

    // First render of this page's lifetime (fresh load OR reload).
    // ALWAYS scroll to top and wipe any stale saved position for this URL
    // so no code path can accidentally restore it.
    if (!HAS_INIT) {
      HAS_INIT = true;
      LAST_KEY = key;
      sessionStorage.removeItem(key);
      window.scrollTo(0, 0);
      // Guard against async image-load reflow pushing the browser back to a
      // remembered scroll position.
      requestAnimationFrame(() => window.scrollTo(0, 0));
      setTimeout(() => window.scrollTo(0, 0), 50);
      setTimeout(() => window.scrollTo(0, 0), 300);
      return;
    }

    // StrictMode remount or same-URL re-render: ignore. Only act on actual
    // route transitions.
    if (key === LAST_KEY) return;
    LAST_KEY = key;

    if (navType === "POP") {
      const saved = sessionStorage.getItem(key);
      const target = saved != null ? parseInt(saved, 10) : NaN;
      if (!Number.isNaN(target) && target > 0) {
        // Retry across frames so lazy-loading images / async-fetched product
        // cards don't clip the restore. Catalog pages render skeletons first
        // (short body) then grow as products arrive — we observe body resize
        // and bump the deadline each time new content lands.
        let deadline = Date.now() + 5000;
        let done = false;
        let ro = null;
        const finish = () => {
          done = true;
          if (ro) ro.disconnect();
        };
        const apply = () => {
          if (done) return;
          const maxScroll = Math.max(
            0,
            document.documentElement.scrollHeight - window.innerHeight,
          );
          const dest = Math.min(target, maxScroll);
          if (window.scrollY !== dest) window.scrollTo(0, dest);
          const reached =
            maxScroll >= target && Math.abs(window.scrollY - target) < 2;
          if (reached || Date.now() > deadline) {
            finish();
            return;
          }
          requestAnimationFrame(apply);
        };
        if (typeof ResizeObserver !== "undefined") {
          ro = new ResizeObserver(() => {
            // Content grew → give ourselves ~1.5s more to settle on the right
            // scroll position after the new frame paints.
            deadline = Math.max(deadline, Date.now() + 1500);
          });
          ro.observe(document.documentElement);
        }
        // Hard safety cap — stop observing after 10 s no matter what.
        setTimeout(finish, 10000);
        requestAnimationFrame(apply);
      } else {
        window.scrollTo(0, 0);
      }
    } else if (hash) {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "instant", block: "start" });
      else window.scrollTo(0, 0);
    } else {
      // PUSH / REPLACE → top
      window.scrollTo(0, 0);
    }
  }, [pathname, search, hash, navType]);

  return null;
}
